import moment from "moment";
import "../../../utils/momentDe"; // zentrale de-Locale-Quelle (Wochentagskürzel)
import { findUserBy, labelForUserKey, userDisplay, type AppUser } from "../../../services/userService";
import type { AustauschDto, AustauschTeilnehmer } from "../../../services/vorgangAustauschService";
import { kuerzelVon } from "../shared/people";

/**
 * Anzeige-Regeln des Austauschs (T-302) — DOM-frei, damit der A-Knopf im
 * Vorgang und die Austausch-Spalte der Vorgangsliste dieselben Wörter benutzen.
 *
 * Seit 01.11.2026 (PO): Ein Austausch sagt nur noch DASS und MIT WEM —
 * mehrere Kolleginnen und Kollegen, auch frei eingetippte Externe. Datum,
 * Uhrzeit und Notiz gibt es nicht mehr; alte Zeilen bringen sie im Verlauf
 * noch mit.
 */

/**
 * „Fr., 30.10., 10:00" — das Jahr nur, wenn es nicht das laufende ist (oder
 * `mitJahr`). Gebraucht nur noch für den Verlauf (wann erledigt).
 */
export function austauschZeitText(iso: string | null | undefined, mitJahr = false, jetzt: Date = new Date()): string {
    if (!iso) return "";
    const m = moment(iso);
    if (!m.isValid()) return "";
    const jahr = mitJahr || m.year() !== jetzt.getFullYear();
    return m.format(jahr ? "dd., DD.MM.YYYY, HH:mm" : "dd., DD.MM., HH:mm");
}

/** Name und Kürzel einer Kollegin — dieselbe Kürzel-Regel wie überall (`kuerzelVon`). */
export function austauschPerson(mitUserId: string, users: AppUser[]): { name: string; kuerzel: string; userId: string | null } {
    const u = findUserBy(users, mitUserId);
    const name = u ? userDisplay(u) : labelForUserKey(mitUserId, users);
    return { name, kuerzel: kuerzelVon(u, name), userId: u?.id ?? null };
}

/** Ein Teilnehmer, fertig zum Anzeigen. */
export interface TeilnehmerAnzeige {
    /** Stabiler Schlüssel (`u:<id>` bzw. `n:<name klein>`), auch zum Entdoppeln. */
    key: string;
    name: string;
    /** Kürzel der Kollegin; bei Externen der Name selbst. */
    kurz: string;
    userId: string | null;
    extern: boolean;
}

export const teilnehmerKey = (t: AustauschTeilnehmer): string =>
    "userId" in t ? `u:${t.userId}` : `n:${t.name.trim().toLocaleLowerCase("de")}`;

export function teilnehmerAnzeige(t: AustauschTeilnehmer, users: AppUser[]): TeilnehmerAnzeige {
    if ("userId" in t) {
        const p = austauschPerson(t.userId, users);
        return { key: teilnehmerKey(t), name: p.name, kurz: p.kuerzel, userId: p.userId ?? t.userId, extern: false };
    }
    return { key: teilnehmerKey(t), name: t.name, kurz: t.name, userId: null, extern: true };
}

export const teilnehmerAnzeigen = (liste: readonly AustauschTeilnehmer[], users: AppUser[]): TeilnehmerAnzeige[] =>
    liste.map((t) => teilnehmerAnzeige(t, users));

/** „Dora Courtmann (DC), Frau Kraus (extern)" — für Tooltips und Screenreader. */
export function teilnehmerText(liste: readonly TeilnehmerAnzeige[]): string {
    return liste.map((t) => (t.extern ? `${t.name} (extern)` : `${t.name} (${t.kurz})`)).join(", ");
}

/**
 * Kurzform für enge Stellen: „DC, CLL, Frau Kraus +2" — Kürzel der
 * Kollegen, Externe mit Namen; ab `max` Einträgen nur noch die Zahl.
 */
export function teilnehmerKurz(liste: readonly TeilnehmerAnzeige[], max = 3): string {
    const sichtbar = liste.slice(0, max).map((t) => t.kurz);
    const rest = liste.length - sichtbar.length;
    return sichtbar.join(", ") + (rest > 0 ? ` +${rest}` : "");
}

/** Tooltip des A-Knopfs: „Austausch mit Dora Courtmann (DC), Frau Kraus (extern)". */
export function austauschTitel(a: { teilnehmer: readonly AustauschTeilnehmer[] }, users: AppUser[]): string {
    const text = teilnehmerText(teilnehmerAnzeigen(a.teilnehmer, users));
    return text ? `Austausch mit ${text}` : "Austausch";
}

/** Sind zwei Teilnehmerlisten gleich (Reihenfolge egal)? */
export function teilnehmerGleich(a: readonly AustauschTeilnehmer[], b: readonly AustauschTeilnehmer[]): boolean {
    if (a.length !== b.length) return false;
    const ka = new Set(a.map(teilnehmerKey));
    return b.every((t) => ka.has(teilnehmerKey(t)));
}

/** Der offene Austausch eines Strangs — `todoId` null = am Vorgang selbst. */
export function offenerAustausch(liste: readonly AustauschDto[], todoId: number | null): AustauschDto | null {
    return liste.find((a) => (a.todoId ?? null) === todoId && a.erledigtAm == null) ?? null;
}

/** Die erledigten eines Strangs, jüngster zuerst. */
export function erledigteAustausche(liste: readonly AustauschDto[], todoId: number | null): AustauschDto[] {
    return liste
        .filter((a) => (a.todoId ?? null) === todoId && a.erledigtAm != null)
        .sort((a, b) => Date.parse(b.erledigtAm ?? "") - Date.parse(a.erledigtAm ?? ""));
}

/**
 * Kolleginnen und Kollegen nach NAME und KÜRZEL suchen (PO: „R/DC" — im Haus
 * spricht man in Kürzeln). Ein exakt passendes Kürzel steht oben, dann
 * Treffer am Wortanfang, dann der Rest. `ausser` blendet schon Gewählte aus.
 */
export function sucheKollegen(
    users: readonly AppUser[],
    suche: string,
    max = 6,
    ausser: ReadonlySet<string> = new Set(),
): AppUser[] {
    const q = suche.trim().toLowerCase();
    const frei = users.filter((u) => !ausser.has(u.id));
    if (!q) return frei.slice(0, max);
    const rang = (u: AppUser): number => {
        const kuerzel = kuerzelVon(u, userDisplay(u)).toLowerCase();
        const name = userDisplay(u).toLowerCase();
        const voll = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim().toLowerCase();
        const felder = [name, voll, (u.name ?? "").toLowerCase(), (u.email ?? "").toLowerCase()];
        if (kuerzel === q || (u.username ?? "").toLowerCase() === q) return 0;
        if (kuerzel.startsWith(q) || felder.some((f) => f.split(/[\s,]+/).some((w) => w.startsWith(q)))) return 1;
        if (kuerzel.includes(q) || felder.some((f) => f.includes(q))) return 2;
        return -1;
    };
    return frei
        .map((u) => ({ u, r: rang(u) }))
        .filter((x) => x.r >= 0)
        .sort((a, b) => a.r - b.r || userDisplay(a.u).localeCompare(userDisplay(b.u), "de"))
        .slice(0, max)
        .map((x) => x.u);
}

/** Länge eines frei eingetippten Namens — dieselbe Grenze wie der Server. */
export const AUSTAUSCH_NAME_MAX = 120;
/** Mehr Personen nimmt der Server nicht an. */
export const AUSTAUSCH_TEILNEHMER_MAX = 20;

/** Eingetippter Text → Name eines Externen (getrimmt, Leerraum zusammengezogen); `null`, wenn leer. */
export function externerName(text: string): string | null {
    const name = text.trim().replace(/[,;]+$/g, "").replace(/\s+/g, " ").trim();
    return name ? name.slice(0, AUSTAUSCH_NAME_MAX) : null;
}

/** Fügt einen Teilnehmer an, sofern er noch nicht dabei ist. */
export function teilnehmerHinzu(liste: readonly AustauschTeilnehmer[], t: AustauschTeilnehmer): AustauschTeilnehmer[] {
    const k = teilnehmerKey(t);
    if (liste.some((x) => teilnehmerKey(x) === k) || liste.length >= AUSTAUSCH_TEILNEHMER_MAX) return [...liste];
    return [...liste, t];
}
